import React, { useEffect, useState, createContext, useContext } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
// The chat platforms Giga lives inside. These cycle in the home hero headline
// ("...your team in ___") to show Giga isn't locked to a single workspace.
// Order matters — Slack leads since it's the primary positioning. Each carries
// its real brand mark + chip color so the platform reads instantly as a logo,
// not just a word.
type Platform = {
  name: string;
  // Inline brand glyph so it always renders in full color (no CDN fallback).
  logo: React.ReactNode;
  // Brand-tinted "liquid glass" pill: a translucent base tint, a soft border,
  // and a colored glow so each platform reads as its own frosted chip.
  bg: string;
  border: string;
  glow: string;
  text: string;
  // How the matching composer SEND BUTTON is styled, so other UI (e.g. the
  // typewriter prompt card's send button) can mirror the real product:
  //   Slack → solid green, square-ish (rounded-md)
  //   Teams → solid purple, circular (rounded-full)
  send: {
    color: string;
    shape: 'square' | 'round';
  };
  // How the hero's prompt card should look when this platform is showing, so
  // the composer reads as that product's composer rather than one generic card
  // with a recoloured button. Slack is a bordered white box, ChatGPT a light
  // grey field, Claude its warm cream — the three surfaces people recognise.
  composer: {
    surface: string;
    border: string;
    caret: string;
  };
};
const SlackGlyph = () => <svg viewBox="0 0 24 24" className="h-[0.7em] w-[0.7em] shrink-0" aria-hidden="true" data-id="element-2108" data-mp="3l5mhf8jpw8m2">
    <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zm1.271 0a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313z" fill="#E01E5A" data-id="element-2109" data-mp="3g45hpgcyys72" />
    <path d="M8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zm0 1.271a2.527 2.527 0 0 1 2.521 2.521 2.527 2.527 0 0 1-2.521 2.521H2.522A2.527 2.527 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312z" fill="#36C5F0" data-id="element-2110" data-mp="20nwydnmyutl6" />
    <path d="M18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522v-2.521zm-1.272 0a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.161 0a2.528 2.528 0 0 1 2.523 2.522v6.312z" fill="#2EB67D" data-id="element-2111" data-mp="1s8h4453o4su7" />
    <path d="M15.161 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.161 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zm0-1.272a2.527 2.527 0 0 1-2.52-2.521 2.526 2.526 0 0 1 2.52-2.522h6.317A2.527 2.527 0 0 1 24 15.163a2.528 2.528 0 0 1-2.522 2.521h-6.317z" fill="#ECB22E" data-id="element-2112" data-mp="1o63z5iwqwyyh" />
  </svg>;
const TeamsGlyph = () => <svg viewBox="0 0 2228.833 2073.333" className="h-[0.7em] w-[0.7em] shrink-0" aria-hidden="true" data-id="element-2113" data-mp="10g9ptbbmxsiz">
    <path fill="#5059C9" d="M1554.637 777.5h575.713c54.391 0 98.483 44.092 98.483 98.483v524.398c0 199.901-162.051 361.952-361.952 361.952h-1.711c-199.901.028-361.975-162-362.004-361.901V828.971c.001-28.427 23.045-51.471 51.471-51.471z" data-id="element-2114" data-mp="14imrnon9h6ah" />
    <circle fill="#5059C9" cx="1943.75" cy="440.583" r="233.25" data-id="element-2115" data-mp="gwu9gjxr8fq4" />
    <circle fill="#7B83EB" cx="1218.083" cy="336.917" r="336.917" data-id="element-2116" data-mp="uv0x778zozc2" />
    <path fill="#7B83EB" d="M1667.323 777.5H717.01c-53.743 1.33-96.257 45.931-95.01 99.676v598.105c-7.505 322.519 247.657 590.16 570.167 598.053 322.51-7.893 577.671-275.534 570.167-598.053V877.176c1.245-53.745-41.268-98.346-95.011-99.676z" data-id="element-2117" data-mp="pvo3c2jparzz" />
    <path opacity=".1" d="M1244 777.5v838.145c-.258 38.435-23.549 72.964-59.09 87.598a91.856 91.856 0 0 1-35.967 7.257H667.613c-6.738-17.105-12.958-34.21-18.142-51.833a631.287 631.287 0 0 1-27.472-183.49V877.02c-1.246-53.659 41.198-98.19 94.855-99.52H1244z" data-id="element-2118" data-mp="zt2i7xpz78q6" />
    <path opacity=".2" d="M1192.167 777.5v889.978a91.802 91.802 0 0 1-7.257 35.967c-14.634 35.541-49.163 58.833-87.598 59.09H691.975c-8.812-17.105-17.105-34.21-24.362-51.833-7.257-17.623-12.958-34.21-18.142-51.833a631.287 631.287 0 0 1-27.472-183.49V877.02c-1.246-53.659 41.198-98.19 94.855-99.52h475.313z" data-id="element-2119" data-mp="1nj6l22vcb4g4" />
    <path opacity=".2" d="M1192.167 777.5v786.312c-.395 52.223-42.632 94.46-94.855 94.855H649.472a631.287 631.287 0 0 1-27.472-183.49V877.02c-1.246-53.659 41.198-98.19 94.855-99.52h475.312z" data-id="element-2120" data-mp="26njhsboywg4f" />
    <path opacity=".2" d="M1140.333 777.5v786.312c-.395 52.223-42.632 94.46-94.855 94.855H649.472a631.287 631.287 0 0 1-27.472-183.49V877.02c-1.246-53.659 41.198-98.19 94.855-99.52h423.478z" data-id="element-2121" data-mp="2gdl6n5p5a2ke" />
    <path opacity=".1" d="M1244 509.522v163.275c-8.812.518-17.105 1.037-25.917 1.037-8.812 0-17.105-.518-25.917-1.037a286.747 286.747 0 0 1-51.833-10.371c-104.226-24.674-190.105-97.794-231.265-196.973a288.02 288.02 0 0 1-16.587-51.833h261.265c52.305.198 94.657 42.549 94.854 94.855z" data-id="element-2122" data-mp="1noxgowtgap0i" />
    <path opacity=".2" d="M1192.167 561.355v111.442a286.747 286.747 0 0 1-51.833-10.371c-104.226-24.674-190.105-97.794-231.265-196.973h188.244c52.305.198 94.656 42.55 94.854 94.902z" data-id="element-2123" data-mp="1xl0k0601gtp1" />
    <path opacity=".2" d="M1192.167 561.355v111.442a286.747 286.747 0 0 1-51.833-10.371c-104.226-24.674-190.105-97.794-231.265-196.973h188.244c52.305.198 94.656 42.55 94.854 94.902z" data-id="element-2124" data-mp="394mrnnfiqsbu" />
    <path opacity=".2" d="M1140.333 561.355v103.073a286.747 286.747 0 0 1-51.833-10.371c-104.226-24.674-190.105-97.794-231.265-196.973h136.41c52.306.198 94.658 42.549 94.855 94.902z" data-id="element-2125" data-mp="sglp9umeevgf" />
    <linearGradient id="teamsg" gradientUnits="userSpaceOnUse" x1="198.099" y1="1683.0726" x2="942.2344" y2="394.2607" gradientTransform="matrix(1 0 0 -1 0 2075.3333)" data-id="element-2126" data-mp="ipish50i52my">
      <stop offset="0" stopColor="#5a62c3" data-id="element-2127" data-mp="9p2pdwno7xim" />
      <stop offset=".5" stopColor="#4d55bd" data-id="element-2128" data-mp="9oasmiic7tqr" />
      <stop offset="1" stopColor="#3940ab" data-id="element-2129" data-mp="4q9embzrqkm5" />
    </linearGradient>
    <path fill="url(#teamsg)" d="M95.01 561.355h950.312c52.473 0 95.01 42.538 95.01 95.01v950.312c0 52.473-42.538 95.01-95.01 95.01H95.01c-52.473 0-95.01-42.538-95.01-95.01V656.365c0-52.472 42.538-95.01 95.01-95.01z" data-id="element-2130" data-mp="4slriaqo64sc" />
    <path fill="#fff" d="M820.211 922.038H630.241v517.297H509.211V922.038H320.123V821.456h500.088z" data-id="element-2131" data-mp="1u5fr5ky570xd" />
  </svg>;
const ChatGPTGlyph = () => <svg viewBox="0 0 24 24" className="h-[0.7em] w-[0.7em] shrink-0" aria-hidden="true" data-id="element-2132" data-mp="3okmmbt8qvv8d">
    <path fill="#0D0D0D" d="M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 2.3408 7.872zm16.5963 3.8558L13.1038 8.364 15.1192 7.2a.0757.0757 0 0 1 .071 0l4.8303 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.407-.667zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1638a.0804.0804 0 0 1-.038-.0567V6.0742a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9994l-2.5974 1.4997-2.6067-1.4997Z" data-id="element-2133" data-mp="3tcnub2yvqzsl" />
  </svg>;
const ClaudeGlyph = () => <svg viewBox="0 0 24 24" className="h-[0.7em] w-[0.7em] shrink-0" aria-hidden="true" data-id="element-2134" data-mp="2aehsrqyw7vry">
    <path fill="#D97757" d="M4.709 15.955l4.72-2.647.079-.23-.08-.128H9.2l-.79-.048-2.698-.073-2.339-.097-2.266-.122-.571-.121L0 11.784l.055-.352.48-.321.686.06 1.52.103 2.278.158 1.652.097 2.449.255h.389l.055-.157-.134-.098-.103-.097-2.358-1.596-2.552-1.688-1.337-.972-.724-.491-.364-.462-.158-1.008.656-.722.881.06.225.061.893.686 1.908 1.476 2.491 1.833.365.304.145-.103.019-.073-.164-.274-1.355-2.446-1.446-2.49-.644-1.032-.17-.619a2.97 2.97 0 01-.104-.729L6.283.134 6.696 0l.996.134.42.364.62 1.414 1.002 2.229 1.555 3.03.456.898.243.832.091.255h.158V9.01l.128-1.706.237-2.095.23-2.695.08-.76.376-.91.747-.492.584.28.48.685-.067.444-.286 1.851-.559 2.903-.364 1.942h.212l.243-.242.985-1.306 1.652-2.064.73-.82.85-.904.547-.431h1.033l.76 1.129-.34 1.166-1.064 1.347-.881 1.142-1.264 1.7-.79 1.36.073.11.188-.02 2.856-.606 1.543-.28 1.841-.315.833.388.091.395-.328.807-1.969.486-2.309.462-3.439.813-.042.03.048.061 1.549.146.662.036h1.622l3.02.225.79.522.474.638-.079.485-1.215.62-1.64-.389-3.829-.911-1.312-.328h-.182v.11l1.093 1.068 2.006 1.81 2.509 2.33.127.578-.322.455-.34-.049-2.205-1.657-.851-.747-1.926-1.62h-.128v.17l.444.649 2.345 3.521.122 1.08-.17.353-.607.213-.668-.122-1.374-1.925-1.415-2.167-1.143-1.943-.14.08-.674 7.254-.316.37-.729.28-.607-.461-.322-.747.322-1.476.389-1.924.315-1.53.286-1.9.17-.632-.012-.042-.14.018-1.434 1.967-2.18 2.945-1.726 1.845-.414.164-.717-.37.067-.662.401-.589 2.388-3.036 1.44-1.882.93-1.086-.006-.158h-.055L4.132 18.56l-1.13.146-.487-.456.061-.746.231-.243 1.908-1.312-.006.006z" data-id="element-2135" data-mp="25lye0qogyulk" />
  </svg>;
// ChatGPT leads, then Claude, then Slack. Slack sat first for as long as it was
// the positioning; the point now is that Giga is reachable from whichever AI a
// team already opens, so no single surface gets the front of the line.
export const PLATFORMS: Platform[] = [{
  name: 'ChatGPT',
  logo: <ChatGPTGlyph data-id="element-2136" data-mp="173ba2n1mqnc" />,
  bg: '#EFEFEF',
  border: 'rgba(13,13,13,0.16)',
  glow: 'rgba(13,13,13,0.14)',
  text: '#0D0D0D',
  // ChatGPT's composer: black, circular send button.
  send: {
    color: '#0D0D0D',
    shape: 'round'
  },
  composer: {
    surface: '#FFFFFF',
    border: 'rgba(13,13,13,0.12)',
    caret: '#0D0D0D'
  }
}, {
  name: 'Claude',
  logo: <ClaudeGlyph data-id="element-2137" data-mp="k2tgrzu6q55z" />,
  bg: '#F6EBE3',
  border: 'rgba(191,94,58,0.26)',
  glow: 'rgba(191,94,58,0.2)',
  text: '#A6462A',
  // Claude's composer: terracotta, circular send button.
  send: {
    color: '#D97757',
    shape: 'round'
  },
  composer: {
    surface: '#FDF8F3',
    border: 'rgba(191,94,58,0.18)',
    caret: '#D97757'
  }
}, {
  name: 'Slack',
  logo: <SlackGlyph data-id="element-2138" data-mp="7ivepdq0cfnm" />,
  // Opaque, light aubergine-tinted base — reads as a frosted glass pane but
  // is fully solid so the dotted background never shows through.
  bg: '#F0EAF2',
  border: 'rgba(74,21,75,0.22)',
  glow: 'rgba(74,21,75,0.18)',
  text: '#4A154B',
  // Slack's send button: green, square-ish.
  send: {
    color: '#007a5a',
    shape: 'square'
  },
  composer: {
    surface: '#FFFFFF',
    border: 'rgba(74,21,75,0.14)',
    caret: '#007a5a'
  }
}];
// ── Shared hero cadence ──────────────────────────────────────────────────
// The hero headline has TWO animated lockups — the tool-icon stack
// (RotatingStack) on the first line and the platform pill (RotatingPlatform)
// on the second. Cycling them on independent timers made them flicker against
// each other. Instead they share ONE clock and TAKE TURNS: a single beat
// counter ticks on a calm interval, the tools swap on EVEN beats and the
// platform swaps on ODD beats — a call-and-response rhythm where only one
// thing moves at a time. The pill's index is derived from the beat so the
// headline pill and the prompt card's send button still flip together.
//
// HERO_BEAT_MS is the spacing between turns. Each lockup therefore changes
// every 2 beats (≈ one full Slack↔Teams or tool-set swap per 2× this value).
const HERO_BEAT_MS = 1600;
type HeroCadence = {
  // Monotonically increasing beat counter shared by both lockups.
  beat: number;
  // Active platform, derived from the beat (advances on ODD beats).
  index: number;
  platform: Platform;
};
const PlatformCycleContext = createContext<HeroCadence | null>(null);
export function PlatformCycleProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const reduce = useReducedMotion();
  const [beat, setBeat] = useState(0);
  useEffect(() => {
    if (reduce) return;
    const id = setInterval(() => {
      setBeat(b => b + 1);
    }, HERO_BEAT_MS);
    return () => clearInterval(id);
  }, [reduce]);
  // The platform advances once every two beats, on the ODD beats, so it
  // alternates with the tool stack (which advances on the EVEN beats).
  const index = Math.floor((beat + 1) / 2) % PLATFORMS.length;
  return <PlatformCycleContext.Provider value={{
    beat,
    index,
    platform: PLATFORMS[index]
  }} data-id="element-2139" data-mp="36st1wuug09wt">
      {children}
    </PlatformCycleContext.Provider>;
}
// Returns the shared hero cadence (beat + active platform), or null if there
// is no provider above (callers then fall back to their own internal timer).
export function usePlatformCycle(): HeroCadence | null {
  return useContext(PlatformCycleContext);
}
export function RotatingPlatform() {
  const reduce = useReducedMotion();
  // Prefer the shared cycle (so the headline pill and the prompt card's send
  // button flip together); fall back to a private cycle when used standalone.
  const shared = usePlatformCycle();
  const [localIndex, setLocalIndex] = useState(0);
  useEffect(() => {
    if (reduce || shared) return;
    const id = setInterval(() => {
      setLocalIndex(i => (i + 1) % PLATFORMS.length);
    }, 2200);
    return () => clearInterval(id);
  }, [reduce, shared]);
  const index = shared ? shared.index : localIndex;
  return (
    // Reserve the width of the WIDEST platform chip by stacking every option
    // invisibly in the same grid cell. This keeps the total sentence width
    // constant so the preceding words ("...your team in") never reflow or
    // re-center — only the platform chip swaps. The chip is left-anchored so
    // it always starts at the same x and any extra room sits to its right.
    <span className="relative inline-grid place-items-start align-baseline not-italic" data-id="element-2140" data-mp="2regviwvwf6db">
      {/* Width/height sizer: every chip is stacked in the SAME grid cell (each
                         col-start-1/row-start-1), so the reserved box is exactly ONE chip tall
                         and as wide as the WIDEST chip — never two chips tall. This keeps the
                         visible layer's box a single, stable line box. */}
      <span className="invisible col-start-1 row-start-1 grid" aria-hidden="true" data-id="element-2141" data-mp="2535req3gtoor">
        {PLATFORMS.map(p => <span key={p.name} className="col-start-1 row-start-1 block" data-id="element-2142" data-mp="1hdbgwf6mcc7o">
            <PlatformChip platform={p} data-id="element-2143" data-mp="1hbibf37qry19" />
          </span>)}
      </span>
      {/* The visible chip layer. It is ABSOLUTELY laid over the invisible
                             width-reserving sizer above (which holds the WIDEST chip's width),
                             so this layer's box never changes size as chips mount/unmount.
                             Each chip is itself absolutely pinned to the left edge of that box,
                             meaning every chip's x-origin is identical and fixed — a narrower
                             chip (Slack) can no longer shift horizontally when the wider one
                             (Teams) exits. We OVERLAP the outgoing and incoming chips (no
                             `mode="wait"`) so the pill is never empty for a frame. */}
      <span className="col-start-1 row-start-1 block relative" data-id="element-2144" data-mp="2w8kvjoa18xyk">
        <AnimatePresence initial={false} data-id="element-2145" data-mp="30d2czgsx6z66">
          <motion.span key={PLATFORMS[index].name} initial={reduce ? {
            opacity: 0
          } : {
            opacity: 0,
            y: '0.4em',
            filter: 'blur(4px)'
          }} animate={{
            opacity: 1,
            y: 0,
            filter: 'blur(0px)'
          }} exit={reduce ? {
            opacity: 0
          } : {
            opacity: 0,
            y: '-0.4em',
            filter: 'blur(4px)'
          }} transition={{
            duration: 0.45,
            ease: [0.16, 1, 0.3, 1]
          }} className="absolute left-0 top-0 inline-block whitespace-nowrap" data-id="element-2146" data-mp="309wljuwtjl7a">
            <PlatformChip platform={PLATFORMS[index]} data-id="element-2147" data-mp="e4v2zvhx5ba9" />
          </motion.span>
        </AnimatePresence>
      </span>
    </span>
  );
}
// A "liquid glass" pill that pairs the brand logo with the platform name, so
// the headline makes it instantly obvious Giga lives in Slack or Teams. The
// chip is a frosted, brand-tinted pane: a translucent base, a backdrop blur, a
// glossy specular sheen along the top, and a soft brand-colored glow.
function PlatformChip({
  platform
}: {
  platform: Platform;
}) {
  return <span className="relative inline-flex items-center gap-[0.3em] overflow-hidden rounded-[0.55em] px-[0.45em] py-[0.08em] align-baseline not-italic font-semibold leading-none border [backdrop-filter:blur(8px)_saturate(1.4)] [-webkit-backdrop-filter:blur(8px)_saturate(1.4)] text-[0.74em]" style={{
    backgroundColor: platform.bg,
    borderColor: platform.border,
    color: platform.text,
    // Render the pill notably smaller than the surrounding headline
    // (text-[0.74em] above) — since the whole chip is em-relative, this
    // shrinks its padding, logo, and radius together, lowering its overall
    // height. Paired with a slightly larger downward nudge so the top of
    // the pill clears the row of tool icons sitting on the line above and
    // the two never touch.
    transform: 'translateY(0.05em)',
    boxShadow: `0 1px 1px rgba(255,255,255,0.5) inset, 0 -1px 1px rgba(0,0,0,0.04) inset, 0 4px 14px -4px ${platform.glow}, 0 1px 3px rgba(0,0,0,0.06)`
  }} data-id="element-2148" data-mp="3mkxzr67htfjt">
      {/* Glossy specular sheen across the top half — the "liquid glass" gleam */}
      <span aria-hidden="true" className="pointer-events-none absolute inset-0 rounded-[0.55em]" style={{
      background: 'linear-gradient(to bottom, rgba(255,255,255,0.55), rgba(255,255,255,0.08) 45%, rgba(255,255,255,0) 60%)'
    }} data-id="element-2149" data-mp="2fsl3b7ykt34s" />
      {/* Soft highlight blob in the top-left for extra liquid depth */}
      <span aria-hidden="true" className="pointer-events-none absolute -left-[10%] -top-[40%] h-[120%] w-[55%] rounded-full" style={{
      background: 'radial-gradient(closest-side, rgba(255,255,255,0.5), rgba(255,255,255,0) 70%)'
    }} data-id="element-2150" data-mp="3v9llqlhdxhlp" />
      <span className="relative flex items-center" data-id="element-2151" data-mp="36vqaxh8d96jg">{platform.logo}</span>
      <span className="relative" data-id="element-2152" data-mp="3kltshuwawc2k">{platform.name}</span>
    </span>;
}